import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { db } from "@lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  getDocs as getDocsFromCollection,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { InputFieldProfile } from "../common/InputFieldProfile";
import { Timestamp } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";

export const Profile = ({ labelColor = "text-slate-800" }) => {
  const { user, logout } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    golonganDarah: "",
    gender: "",
    negara: "",
    kontakDarurat: "", // Official Number
  });
  const [loading, setLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        if (isLoggingOut || !user) {
          return;
        }

        if (!user.emailVerified) {
          await logout();
          navigate("/login");
          return;
        }

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (!isMounted) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const fullName = data.namaLengkap || user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          setFormData({
            namaDepan,
            namaBelakang: namaBelakang.join(" "),
            nomorIdentitas: data.nomorIdentitas || "",
            tanggalLahir: data.tanggalLahir || "",
            golonganDarah: data.golonganDarah || "",
            gender: data.gender || "",
            negara: data.negara || "",
            kontakDarurat: data.kontakDarurat || "",
          });
        } else {
          const fullName = user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          setFormData({
            namaDepan,
            namaBelakang: namaBelakang.join(" "),
            nomorIdentitas: "",
            tanggalLahir: "",
            golonganDarah: "",
            gender: "",
            negara: "",
            kontakDarurat: "",
          });
        }
      } catch (err) {
        if (!isMounted || isLoggingOut) return;
        console.error("Gagal mengambil data profil:", err);
        showNotification("error", "Failed to fetch user data.");
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [user, logout, navigate, showNotification, isLoggingOut]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullName = `${formData.namaDepan} ${formData.namaBelakang}`.trim();
      if (!formData.namaDepan || !formData.namaBelakang) {
        showNotification("error", "First name and last name cannot be empty!");
        setLoading(false);
        return;
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          ...formData,
          namaLengkap: fullName,
          email: user.email,
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );

      // Attempt to update existing registrations
      const eventsSnapshot = await getDocsFromCollection(
        collection(db, "events")
      );
      let updatedRegistrations = 0;

      const registrationData = {
        namaLengkap: fullName,
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        golonganDarah: formData.golonganDarah,
        gender: formData.gender,
        negara: formData.negara,
        kontakDarurat: formData.kontakDarurat,
        updatedAt: Timestamp.now(),
      };

      for (const eventDoc of eventsSnapshot.docs) {
        const { collectionName } = eventDoc.data();
        if (!collectionName) continue;

        const registrationQuery = query(
          collection(db, collectionName),
          where("uid", "==", user.uid)
        );
        const registrationSnapshot = await getDocs(registrationQuery);

        if (!registrationSnapshot.empty) {
          for (const registrationDoc of registrationSnapshot.docs) {
            await updateDoc(registrationDoc.ref, registrationData);
            updatedRegistrations++;
          }
        }
      }

      if (updatedRegistrations > 0) {
        showNotification(
          "success",
          "Profile and event registration updated successfully!"
        );
      } else {
        showNotification("success", "Profile updated successfully!");
      }
    } catch (err) {
      console.error("Gagal menyimpan data:", err);
      showNotification("error", "An error occurred while saving data!");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    showConfirm(
      "Are you sure you want to logout?",
      async () => {
        setIsLoggingOut(true);
        try {
          await logout();
          navigate("/login");
        } catch (err) {
          setIsLoggingOut(false);
          console.error("Gagal logout:", err);
          showNotification("error", "Failed to logout. Please try again.");
        }
      },
      "Logout",
      "Cancel"
    );
  };

  return (
    <div className="container mx-auto px-4 py-20 flex justify-center">
      <div className="bg-white rounded-xl border border-slate-200 p-4 w-full max-w-6xl">
        <div className="flex justify-center mb-4">
          <img
            src={
              user?.photoURL ||
              "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
            }
            alt="Avatar"
            className="w-24 h-24 rounded-full border-4 border-gray-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
            }}
          />
        </div>
        <p className="text-lg mb-6 text-center text-slate-800">
          Hello,{" "}
          <strong>
            {`${formData.namaDepan} ${formData.namaBelakang}` ||
              user?.displayName ||
              user.email}
          </strong>
        </p>
        <form onSubmit={handleSubmit}>
          <InputFieldProfile
            formData={formData}
            handleChange={handleChange}
            isFormDisabled={loading}
            labelColor={labelColor}
          />
          <div className="flex gap-4 mt-6 justify-center">
            <button
              type="submit"
              className="px-8 py-2 bg-red-900 text-white rounded-xl hover:bg-red-700 transition-colors duration-200 w-full max-w-xs"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Data"}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="px-8 py-2 border border-red-900 text-red-900 rounded-xl hover:bg-red-50 transition-colors duration-200 w-full max-w-xs flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faSignOutAlt} />
              Logout
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
